import { bjtModelParameterMap } from "../../description/device/bjt";
import { diodeModelParameterMap } from "../../description/device/diode";
import { defaultMOSFETParameters, jfetModelParameterMap, mosfetModelParameterMap } from "../../description/device/fet";
import { calculateMutualInductances } from "../../description/device/inductor";
import { ensureNodes } from "../../description/device/instance";
import { mapModelParametersToSpice } from "../../description/device/modelParameters";
import { defaultOpAmpParameters } from "../../description/device/opamp";
import { switchModelParameterMap } from "../../description/device/switch";
import { netlistError, ok, partitionResults } from "../../error";
import { currentVectorName, voltageVectorName } from "../../ngspice";
import { pulseParamString, sinParamString } from "./helpers";
function declareInstance(line) {
    return {
        type: "declareInstance",
        line,
    };
}
function declareSubcircuit(subName, externalNodes, children) {
    return {
        type: "declareSubcircuit",
        subName,
        children,
        externalNodes,
    };
}
function declareModel(line) {
    if (typeof line === "string") {
        return {
            type: "declareModel",
            line,
        };
    }
    else {
        return {
            type: "declareModel",
            line: `.model ${line.modelName} ${line.modelDesignatorPrefix} ${line.modelParams}`,
        };
    }
}
function declareFETModel(decl) {
    return {
        type: "declareModel",
        line: `.model ${decl.modelName} ${decl.type} ${decl.modelParams}`,
    };
}
function resistorInstanceToLines(resistor, context) {
    const ensureResult = ensureNodes(resistor);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    // Get vector names
    const currentVecName = currentVectorName(resistor.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    // Store vector names
    context.addCurrentVectorName(resistor, currentVecName);
    context.addVoltageVectorName(resistor, nPlusVecName, "nPlus");
    context.addVoltageVectorName(resistor, nMinusVecName, "nMinus");
    return ok(declareInstance(`${resistor.designator} ${nPlus} ${nMinus} ${resistor.model.deviceParams.resistance}`));
}
function capacitorInstanceToLines(capacitor, context) {
    const ensureResult = ensureNodes(capacitor);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    const currentVecName = currentVectorName(capacitor.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(capacitor, currentVecName);
    context.addVoltageVectorName(capacitor, nPlusVecName, "nPlus");
    context.addVoltageVectorName(capacitor, nMinusVecName, "nMinus");
    return ok(declareInstance(`${capacitor.designator} ${nPlus} ${nMinus} ${capacitor.model.deviceParams.capacitanceFarads}`));
}
function inductorInstanceToLines(inductor, context) {
    const ensureResult = ensureNodes(inductor);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    // Get vector names
    const currentVecName = currentVectorName(inductor.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    // Store vector names
    context.addCurrentVectorName(inductor, currentVecName);
    context.addVoltageVectorName(inductor, nPlusVecName, "nPlus");
    context.addVoltageVectorName(inductor, nMinusVecName, "nMinus");
    return ok(declareInstance(`${inductor.designator} ${nPlus} ${nMinus} ${inductor.model.deviceParams.inductanceHenries}`));
}
function coupledInductorInstanceToLines(coupledInductor, context) {
    const ensureResult = ensureNodes(coupledInductor);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlusA, nMinusA, nPlusB, nMinusB } = ensureResult.value;
    const [inductorA, inductorB] = coupledInductor.model.deviceParams.inductorDesignators;
    context.addInstanceParameters(coupledInductor.designator, coupledInductor.model.deviceParams);
    const currentVecNameA = currentVectorName(inductorA);
    const currentVecNameB = currentVectorName(inductorB);
    const nPlusVecNameA = voltageVectorName(nPlusA);
    const nMinusVecNameA = voltageVectorName(nMinusA);
    const nPlusVecNameB = voltageVectorName(nPlusB);
    const nMinusVecNameB = voltageVectorName(nMinusB);
    context.addCurrentVectorName(coupledInductor, currentVecNameA, "A");
    context.addCurrentVectorName(coupledInductor, currentVecNameB, "B");
    context.addVoltageVectorName(coupledInductor, nPlusVecNameA, "nPlusA");
    context.addVoltageVectorName(coupledInductor, nMinusVecNameA, "nMinusA");
    context.addVoltageVectorName(coupledInductor, nPlusVecNameB, "nPlusB");
    context.addVoltageVectorName(coupledInductor, nMinusVecNameB, "nMinusB");
    // We first need to declare both inductors, then we can declare the coupling
    const { inductanceHenries, ratio, couplingCoefficient } = coupledInductor.model.deviceParams;
    const [inductanceA, inductanceB] = calculateMutualInductances(inductanceHenries, ratio);
    return ok([
        declareInstance(`${inductorA} ${nPlusA} ${nMinusA} ${inductanceA}`),
        declareInstance(`${inductorB} ${nPlusB} ${nMinusB} ${inductanceB}`),
        declareInstance(`${coupledInductor.designator} ${inductorA} ${inductorB} ${couplingCoefficient}`),
    ]);
}
function diodeInstanceToLines(diode, context) {
    const ensureResult = ensureNodes(diode);
    if (!ensureResult.ok)
        return ensureResult;
    const { anode, cathode } = ensureResult.value;
    // Get vector names
    const currentVecName = currentVectorName(diode.designator);
    const anodeVecName = voltageVectorName(anode);
    const cathodeVecName = voltageVectorName(cathode);
    // Store vector names
    context.addCurrentVectorName(diode, currentVecName);
    context.addVoltageVectorName(diode, anodeVecName, "anode");
    context.addVoltageVectorName(diode, cathodeVecName, "cathode");
    const model = context.getModelDeclaration(diode.model.designatorPrefix, mapModelParametersToSpice(diode.model.deviceParams, diodeModelParameterMap));
    return ok([declareModel(model), declareInstance(`${diode.designator} ${anode} ${cathode} ${model.modelName}`)]);
}
function constantCurrentSourceToLines(ccs, context) {
    const ensureResult = ensureNodes(ccs);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    const currentVecName = currentVectorName(ccs.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(ccs, currentVecName);
    context.addVoltageVectorName(ccs, nPlusVecName, "nPlus");
    context.addVoltageVectorName(ccs, nMinusVecName, "nMinus");
    return ok(declareInstance(`${ccs.designator} ${nPlus} ${nMinus} DC ${ccs.model.deviceParams.amperes}`));
}
function pulsedCurrentSourceToLines(pcs, context) {
    const ensureResult = ensureNodes(pcs);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    context.addInstanceParameters(pcs.designator, pcs.model.deviceParams);
    const currentVecName = currentVectorName(pcs.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(pcs, currentVecName);
    context.addVoltageVectorName(pcs, nPlusVecName, "nPlus");
    context.addVoltageVectorName(pcs, nMinusVecName, "nMinus");
    const paramString = pulseParamString(pcs.model.deviceParams, context.timeStep, context.endTime);
    return ok(declareInstance(`${pcs.designator} ${nPlus} ${nMinus} PULSE(${paramString})`));
}
function sinusoidalCurrentSourceToLines(sincs, context) {
    const ensureResult = ensureNodes(sincs);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    context.addInstanceParameters(sincs.designator, sincs.model.deviceParams);
    const currentVecName = currentVectorName(sincs.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(sincs, currentVecName);
    context.addVoltageVectorName(sincs, nPlusVecName, "nPlus");
    context.addVoltageVectorName(sincs, nMinusVecName, "nMinus");
    const paramString = sinParamString(sincs.model.deviceParams);
    return ok(declareInstance(`${sincs.designator} ${nPlus} ${nMinus} SIN(${paramString})`));
}
function constantVoltageSourceToLines(cvs, context) {
    const ensureResult = ensureNodes(cvs);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    const currentVecName = currentVectorName(cvs.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(cvs, currentVecName);
    context.addVoltageVectorName(cvs, nPlusVecName, "nPlus");
    context.addVoltageVectorName(cvs, nMinusVecName, "nMinus");
    return ok(declareInstance(`${cvs.designator} ${nPlus} ${nMinus} DC ${cvs.model.deviceParams.volts}`));
}
function pulsedVoltageSourceToLines(pvs, context) {
    const ensureResult = ensureNodes(pvs);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    context.addInstanceParameters(pvs.designator, pvs.model.deviceParams);
    const currentVecName = currentVectorName(pvs.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(pvs, currentVecName);
    context.addVoltageVectorName(pvs, nPlusVecName, "nPlus");
    context.addVoltageVectorName(pvs, nMinusVecName, "nMinus");
    const paramString = pulseParamString(pvs.model.deviceParams, context.timeStep, context.endTime);
    return ok(declareInstance(`${pvs.designator} ${nPlus} ${nMinus} PULSE(${paramString})`));
}
function sinusoidalVoltageSourceToLines(sinvs, context) {
    const ensureResult = ensureNodes(sinvs);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    context.addInstanceParameters(sinvs.designator, sinvs.model.deviceParams);
    const currentVecName = currentVectorName(sinvs.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(sinvs, currentVecName);
    context.addVoltageVectorName(sinvs, nPlusVecName, "nPlus");
    context.addVoltageVectorName(sinvs, nMinusVecName, "nMinus");
    const paramString = sinParamString(sinvs.model.deviceParams);
    return ok(declareInstance(`${sinvs.designator} ${nPlus} ${nMinus} SIN(${paramString})`));
}
function voltageControlledVoltageSourceToLines(vcvs, context) {
    const ensureResult = ensureNodes(vcvs);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus, nControlPlus, nControlMinus } = ensureResult.value;
    const currentVecName = currentVectorName(vcvs.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    const nControlPlusVecName = voltageVectorName(nControlPlus);
    const nControlMinusVecName = voltageVectorName(nControlMinus);
    context.addCurrentVectorName(vcvs, currentVecName);
    context.addVoltageVectorName(vcvs, nPlusVecName, "nPlus");
    context.addVoltageVectorName(vcvs, nMinusVecName, "nMinus");
    context.addVoltageVectorName(vcvs, nControlPlusVecName, "nControlPlus");
    context.addVoltageVectorName(vcvs, nControlMinusVecName, "nControlMinus");
    const gain = vcvs.model.deviceParams.voltageGain;
    return ok(declareInstance(`${vcvs.designator} ${nPlus} ${nMinus} ${nControlPlus} ${nControlMinus} ${gain}`));
}
function bjtInstanceToLines(bjt, context) {
    const ensureResult = ensureNodes(bjt);
    if (!ensureResult.ok)
        return ensureResult;
    const { base, collector, emitter } = ensureResult.value;
    const collectorCurrentVecName = currentVectorName(bjt.designator, "c");
    const baseCurrentVecName = currentVectorName(bjt.designator, "b");
    const emitterCurrentVecName = currentVectorName(bjt.designator, "e");
    const collectorVoltageVecName = voltageVectorName(collector);
    const baseVoltageVecName = voltageVectorName(base);
    const emitterVoltageVecName = voltageVectorName(emitter);
    context.addCurrentVectorName(bjt, collectorCurrentVecName, "collector");
    context.addCurrentVectorName(bjt, baseCurrentVecName, "base");
    context.addCurrentVectorName(bjt, emitterCurrentVecName, "emitter");
    context.addVoltageVectorName(bjt, collectorVoltageVecName, "collector");
    context.addVoltageVectorName(bjt, baseVoltageVecName, "base");
    context.addVoltageVectorName(bjt, emitterVoltageVecName, "emitter");
    const model = context.getModelDeclaration(bjt.model.deviceParams.type, mapModelParametersToSpice(bjt.model.deviceParams, bjtModelParameterMap));
    return ok([
        declareModel(model),
        declareInstance(`${bjt.designator} ${collector} ${base} ${emitter} ${model.modelName}`),
    ]);
}
function mosfetInstanceToLines(mosfet, context) {
    const ensureResult = ensureNodes(mosfet);
    if (!ensureResult.ok)
        return ensureResult;
    const { drain, gate, source } = ensureResult.value;
    const drainCurrentVecName = currentVectorName(mosfet.designator, "d");
    const gateCurrentVecName = currentVectorName(mosfet.designator, "g");
    const sourceCurrentVecName = currentVectorName(mosfet.designator, "s");
    const drainVoltageVecName = voltageVectorName(drain);
    const gateVoltageVecName = voltageVectorName(gate);
    const sourceVoltageVecName = voltageVectorName(source);
    context.addCurrentVectorName(mosfet, drainCurrentVecName, "drain");
    context.addCurrentVectorName(mosfet, gateCurrentVecName, "gate");
    context.addCurrentVectorName(mosfet, sourceCurrentVecName, "source");
    context.addVoltageVectorName(mosfet, drainVoltageVecName, "drain");
    context.addVoltageVectorName(mosfet, gateVoltageVecName, "gate");
    context.addVoltageVectorName(mosfet, sourceVoltageVecName, "source");
    const model = context.getFETModelDeclaration("mosfet", mosfet.model.deviceParams.type, mapModelParametersToSpice(mosfet.model.deviceParams, mosfetModelParameterMap));
    return ok([
        declareFETModel(model),
        // Note: bulk is connected to source here
        declareInstance(`${mosfet.designator} ${drain} ${gate} ${source} ${source} ${model.modelName}`),
    ]);
}
function jfetInstanceToLines(jfet, context) {
    const ensureResult = ensureNodes(jfet);
    if (!ensureResult.ok)
        return ensureResult;
    const { drain, gate, source } = ensureResult.value;
    const drainCurrentVecName = currentVectorName(jfet.designator, "d");
    const gateCurrentVecName = currentVectorName(jfet.designator, "g");
    const sourceCurrentVecName = currentVectorName(jfet.designator, "s");
    const drainVoltageVecName = voltageVectorName(drain);
    const gateVoltageVecName = voltageVectorName(gate);
    const sourceVoltageVecName = voltageVectorName(source);
    context.addCurrentVectorName(jfet, drainCurrentVecName, "drain");
    context.addCurrentVectorName(jfet, gateCurrentVecName, "gate");
    context.addCurrentVectorName(jfet, sourceCurrentVecName, "source");
    context.addVoltageVectorName(jfet, drainVoltageVecName, "drain");
    context.addVoltageVectorName(jfet, gateVoltageVecName, "gate");
    context.addVoltageVectorName(jfet, sourceVoltageVecName, "source");
    const model = context.getFETModelDeclaration("jfet", jfet.model.deviceParams.type, mapModelParametersToSpice(jfet.model.deviceParams, jfetModelParameterMap));
    return ok([
        declareFETModel(model),
        declareInstance(`${jfet.designator} ${drain} ${gate} ${source} ${model.modelName}`),
    ]);
}
function switchInstanceToLines(sw, context) {
    const ensureResult = ensureNodes(sw);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    const subCircuit = context.getSubcircuitDeclaration("spst", 2);
    // Store subcircuit instance designator
    context.addSubcircuitInstanceDesignator(sw.designator, subCircuit.designator);
    // Get vector names
    const currentVecName = currentVectorName(subCircuit.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    // Store vector names
    context.addCurrentVectorName(sw, currentVecName);
    context.addVoltageVectorName(sw, nPlusVecName, "nPlus");
    context.addVoltageVectorName(sw, nMinusVecName, "nMinus");
    // NOTE: Here we inject the voltage threshold to get correct behavior.
    const switchModel = context.getModelDeclaration("sw", {
        ...mapModelParametersToSpice(sw.model.deviceParams, switchModelParameterMap),
        vt: 1,
    });
    const initialControlVoltage = sw.model.deviceParams.state === "on" ? 5 : 0;
    return ok([
        declareModel(switchModel),
        declareSubcircuit(subCircuit.subName, subCircuit.externalNodes, [
            declareInstance(`S0 ${subCircuit.externalNodes[0]} ${subCircuit.externalNodes[1]} 3 5 ${switchModel.modelName} ${sw.model.deviceParams.state.toUpperCase()}`),
            declareInstance(`R0 4 5 1k`),
            declareInstance(`V0 3 4 DC ${initialControlVoltage}`),
        ]),
        declareInstance(`${subCircuit.designator} ${nPlus} ${nMinus} ${subCircuit.subName}`),
    ]);
}
function spdtSwitchInstanceToLines(sw, context) {
    var _a, _b;
    const ensureResult = ensureNodes(sw);
    if (!ensureResult.ok)
        return ensureResult;
    const { input1, input2, common } = ensureResult.value;
    const subcircuit = context.getSubcircuitDeclaration("spdt", 3);
    // Store subcircuit instance designator
    context.addSubcircuitInstanceDesignator(sw.designator, subcircuit.designator);
    // Store instance parameters
    context.addInstanceParameters(sw.designator, sw.model.deviceParams);
    const [input1Node, input2Node, commonNode] = subcircuit.externalNodes;
    // Note: 0 is ground which won't be used, so regular boolean check is sufficient
    if (!input1Node || !input2Node || !commonNode) {
        throw new Error("SPDT switch subcircuit does not have enough external nodes");
    }
    // Get vector names
    const input1CurrentVecName = currentVectorName(subcircuit.designator, input1Node.toString());
    const input2CurrentVecName = currentVectorName(subcircuit.designator, input2Node.toString());
    const commonCurrentVecName = currentVectorName(subcircuit.designator, commonNode.toString());
    const input1VoltageVecName = voltageVectorName(input1);
    const input2VoltageVecName = voltageVectorName(input2);
    const commonVoltageVecName = voltageVectorName(common);
    // Store vector names
    context.addCurrentVectorName(sw, input1CurrentVecName, "input1");
    context.addCurrentVectorName(sw, input2CurrentVecName, "input2");
    context.addCurrentVectorName(sw, commonCurrentVecName, "common");
    context.addVoltageVectorName(sw, input1VoltageVecName, "input1");
    context.addVoltageVectorName(sw, input2VoltageVecName, "input2");
    context.addVoltageVectorName(sw, commonVoltageVecName, "common");
    // NOTE: Here we inject the resistances to get correct behavior
    // onResistance: resistance between input and common when on (default 1)
    const onResistance = (_a = sw.model.deviceParams.onResistance) !== null && _a !== void 0 ? _a : 1;
    // offResistance: resistance between input and common when off (default 1e13 ≈ ∞)
    const offResistance = (_b = sw.model.deviceParams.offResistance) !== null && _b !== void 0 ? _b : 1e13;
    // state: "on" is input1 -> common, "off" is input2 -> common
    const resistance1 = sw.model.deviceParams.state === "on" ? onResistance : offResistance;
    const resistance2 = sw.model.deviceParams.state === "off" ? onResistance : offResistance;
    return ok([
        declareSubcircuit(subcircuit.subName, subcircuit.externalNodes, [
            declareInstance(`R0 ${subcircuit.externalNodes[0]} ${subcircuit.externalNodes[2]} ${resistance1}`),
            declareInstance(`R1 ${subcircuit.externalNodes[1]} ${subcircuit.externalNodes[2]} ${resistance2}`),
        ]),
        declareInstance(`${subcircuit.designator} ${input1} ${input2} ${common} ${subcircuit.subName}`),
    ]);
}
function voltageControlledSwitchInstanceToLines(sw, context) {
    const ensureResult = ensureNodes(sw);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus, controlPlus, controlMinus } = ensureResult.value;
    const currentVecName = currentVectorName(sw.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    const controlPlusVecName = voltageVectorName(controlPlus);
    const controlMinusVecName = voltageVectorName(controlMinus);
    context.addCurrentVectorName(sw, currentVecName);
    context.addVoltageVectorName(sw, nPlusVecName, "nPlus");
    context.addVoltageVectorName(sw, nMinusVecName, "nMinus");
    context.addVoltageVectorName(sw, controlPlusVecName, "controlPlus");
    context.addVoltageVectorName(sw, controlMinusVecName, "controlMinus");
    const switchModel = context.getModelDeclaration("sw", mapModelParametersToSpice(sw.model.deviceParams, switchModelParameterMap));
    return ok([
        declareModel(switchModel),
        declareInstance(`${sw.designator} ${nPlus} ${nMinus} ${controlPlus} ${controlMinus} ${switchModel.modelName}`),
    ]);
}
function ammeterInstanceToLines(ammeter, context) {
    // const {nPlus, nMinus} = ensureNodes(ammeter);
    const ensureResult = ensureNodes(ammeter);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    const currentVecName = currentVectorName(ammeter.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(ammeter, currentVecName);
    context.addVoltageVectorName(ammeter, nPlusVecName, "nPlus");
    context.addVoltageVectorName(ammeter, nMinusVecName, "nMinus");
    // Ammeter is a voltage source with 0V
    return ok(declareInstance(`${ammeter.designator} ${nPlus} ${nMinus} DC 0`));
}
function externalInstanceToLines(device, context) {
    const ensureResult = ensureNodes(device);
    if (!ensureResult.ok)
        return ensureResult;
    const nodeAssignments = ensureResult.value;
    const subcircuit = context.getSubcircuitDeclaration("external", Object.keys(nodeAssignments).length);
    // Store subcircuit instance designator
    context.addSubcircuitInstanceDesignator(device.designator, subcircuit.designator);
    // Note: Spice only outputs node current for devices with >2 nodes, otherwise it outputs
    // device current only. So we only store node current if there are more than 2 nodes.
    const shouldStoreNodeCurrent = Object.keys(nodeAssignments).length > 2;
    // TODO: Handle case where an input and output node have the same name.
    //       The input node number will be overwritten in the subcircuit node map.
    const subcircuitChildren = [];
    // For each input, generate a shunt resistor in the subcircuit (to be used for measurement and to drain input)
    // The resistor should be connected to the input node and the ground node
    device.model.deviceParams.inputs.forEach((nodeName, i) => {
        i += 1; // Offset by 1 to avoid ground node
        subcircuitChildren.push(declareInstance(`R${i} ${i} 0 1e13`));
        context.addSubcircuitNode(device.designator, nodeName, i); // Store node number for later use
        // Get and store current vector name
        const vecNodeName = shouldStoreNodeCurrent ? i.toString() : undefined;
        const currentVecName = currentVectorName(subcircuit.designator, vecNodeName);
        context.addCurrentVectorName(device, currentVecName, nodeName);
    });
    // For each output, generate a voltage source (w/ shunt resistor) and current source in the subcircuit
    // The pair of sources should be connected to the same node (the output node)
    Object.entries(device.model.deviceParams.outputs).forEach(([nodeName, output], i) => {
        var _a, _b;
        i += device.model.deviceParams.inputs.length + 1; // Offset by number of inputs and ground node
        const initialVoltage = (_a = output.voltage) !== null && _a !== void 0 ? _a : 0;
        const initialCurrent = (_b = output.current) !== null && _b !== void 0 ? _b : 0;
        // We can't properly set both a voltage and current, so we use a resistor to override
        // voltage if a current is set. Note: this only needs to be done for the voltage source,
        // since the current source is naturally overridden by the voltage source.
        const initialResistance = !!initialCurrent ? 1e13 : 1e-8;
        subcircuitChildren.push(declareInstance(`R${i} inner${i} 0 ${initialResistance}`));
        subcircuitChildren.push(declareInstance(`V${i} ${i} inner${i} DC ${initialVoltage}`));
        subcircuitChildren.push(declareInstance(`I${i} 0 ${i} DC ${initialCurrent}`));
        context.addSubcircuitNode(device.designator, nodeName, i); // Store node number for later use
        // Get and store current vector name
        const vecNodeName = shouldStoreNodeCurrent ? i.toString() : undefined;
        const currentVecName = currentVectorName(subcircuit.designator, vecNodeName);
        context.addCurrentVectorName(device, currentVecName, nodeName);
    });
    // Get and store voltage vector names
    for (const [nodeName, nodeNumber] of Object.entries(nodeAssignments)) {
        const voltageVecName = voltageVectorName(nodeNumber);
        context.addVoltageVectorName(device, voltageVecName, nodeName);
    }
    return ok([
        declareSubcircuit(subcircuit.subName, subcircuit.externalNodes, subcircuitChildren),
        declareInstance(`${subcircuit.designator} ${Object.values(nodeAssignments).join(" ")} ${subcircuit.subName}`),
    ]);
}
function inverterInstanceToLines(device, context) {
    const ensureResult = ensureNodes(device);
    if (!ensureResult.ok)
        return ensureResult;
    const { input, output } = ensureResult.value;
    const nmosModel = context.getFETModelDeclaration("mosfet", "nmos", mapModelParametersToSpice(defaultMOSFETParameters, mosfetModelParameterMap));
    const pmosModel = context.getFETModelDeclaration("mosfet", "pmos", mapModelParametersToSpice(defaultMOSFETParameters, mosfetModelParameterMap));
    const subcircuit = context.getSubcircuitDeclaration("inverter", 2);
    context.addSubcircuitInstanceDesignator(device.designator, subcircuit.designator);
    // Build the subcircuit
    // mosfet decl: M<name> <drain> <gate> <source> <bulk> <model>
    // 1 nmos, 1 pmos
    // input is connected to both gates
    // output is connected to both drains
    // nmos source is connected to ground
    // pmos source is connected to vdd
    // source is connected to bulk on both
    // input = subcircuit.externalNodes[0]
    // output = subcircuit.externalNodes[1]
    const subcircuitChildren = [
        declareInstance(`MN1 ${subcircuit.externalNodes[1]} ${subcircuit.externalNodes[0]} 0 0 ${nmosModel.modelName}`),
        declareInstance(`MP1 ${subcircuit.externalNodes[1]} ${subcircuit.externalNodes[0]} vdd vdd ${pmosModel.modelName}`),
        declareInstance(`V0 vdd 0 DC ${device.model.deviceParams.highVoltage}`),
    ];
    // Get and store vector names
    const currentVecName = currentVectorName(subcircuit.designator);
    const inputVoltageVecName = voltageVectorName(input);
    const outputVoltageVecName = voltageVectorName(output);
    context.addCurrentVectorName(device, currentVecName);
    context.addVoltageVectorName(device, inputVoltageVecName, "input");
    context.addVoltageVectorName(device, outputVoltageVecName, "output");
    return ok([
        declareFETModel(nmosModel),
        declareFETModel(pmosModel),
        declareSubcircuit(subcircuit.subName, subcircuit.externalNodes, subcircuitChildren),
        declareInstance(`${subcircuit.designator} ${input} ${output} ${subcircuit.subName}`),
    ]);
}
function gateInstanceToLines(device, context) {
    const ensureResult = ensureNodes(device);
    if (!ensureResult.ok)
        return ensureResult;
    const { input1, input2, output } = ensureResult.value;
    // All gates need a model declaration for NMOS and PMOS
    const nmosModel = context.getFETModelDeclaration("mosfet", "nmos", mapModelParametersToSpice(defaultMOSFETParameters, mosfetModelParameterMap));
    const pmosModel = context.getFETModelDeclaration("mosfet", "pmos", mapModelParametersToSpice(defaultMOSFETParameters, mosfetModelParameterMap));
    // Create a subcircuit for the gate
    const subcircuit = context.getSubcircuitDeclaration(device.model.deviceParams.type, 3);
    context.addSubcircuitInstanceDesignator(device.designator, subcircuit.designator);
    let subcircuitChildren;
    const [input1Node, input2Node, outputNode] = subcircuit.externalNodes;
    // Note: 0 is ground which won't be used, so regular boolean check is sufficient
    if (!input1Node || !input2Node || !outputNode) {
        throw new Error("Gate subcircuit does not have enough external nodes");
    }
    // Build the subcircuit based on the gate type
    // mosfet decl: M<name> <drain> <gate> <source> <bulk> <model>
    switch (device.model.deviceParams.type) {
        case "andGate":
            subcircuitChildren = [
                // NAND
                declareInstance(`MP1 nandout ${input1Node} vdd vdd ${pmosModel.modelName}`),
                declareInstance(`MN1 nandout ${input1Node} 99 99 ${nmosModel.modelName}`),
                declareInstance(`MN2 99 ${input2Node} 0 0 ${nmosModel.modelName}`),
                declareInstance(`MP2 nandout ${input2Node} vdd vdd ${pmosModel.modelName}`),
                // Inverter
                declareInstance(`MN3 ${outputNode} nandout 0 0 ${nmosModel.modelName}`),
                declareInstance(`MP3 ${outputNode} nandout vdd vdd ${pmosModel.modelName}`),
            ];
            break;
        case "orGate":
            subcircuitChildren = [
                // NOR
                declareInstance(`MN1 norout ${input1Node} 0 0 ${nmosModel.modelName}`),
                declareInstance(`MN2 norout ${input2Node} 0 0 ${nmosModel.modelName}`),
                declareInstance(`MP1 norout ${input1Node} 99 99 ${pmosModel.modelName}`),
                declareInstance(`MP2 99 ${input2Node} vdd vdd ${pmosModel.modelName}`),
                // Inverter
                declareInstance(`MN3 ${outputNode} norout 0 0 ${nmosModel.modelName}`),
                declareInstance(`MP3 ${outputNode} norout vdd vdd ${pmosModel.modelName}`),
            ];
            break;
        case "xOrGate":
            subcircuitChildren = [
                // Inverter for first input
                declareInstance(`MNinv1 not1 ${input1Node} 0 0 ${nmosModel.modelName}`),
                declareInstance(`MPinv1 not1 ${input1Node} vdd vdd ${pmosModel.modelName}`),
                // Inverter for second input
                declareInstance(`MNinv2 not2 ${input2Node} 0 0 ${nmosModel.modelName}`),
                declareInstance(`MPinv2 not2 ${input2Node} vdd vdd ${pmosModel.modelName}`),
                // XOR gate
                declareInstance(`MP1 99 not1 vdd vdd ${pmosModel.modelName}`),
                declareInstance(`MP2 ${outputNode} ${input2Node} 99 99 ${pmosModel.modelName}`),
                declareInstance(`MP3 98 ${input1Node} vdd vdd ${pmosModel.modelName}`),
                declareInstance(`MP4 ${outputNode} not2 98 98 ${pmosModel.modelName}`),
                declareInstance(`MN1 ${outputNode} ${input1Node} 97 97 ${nmosModel.modelName}`),
                declareInstance(`MN2 97 ${input2Node} 0 0 ${nmosModel.modelName}`),
                declareInstance(`MN3 ${outputNode} not1 96 96 ${nmosModel.modelName}`),
                declareInstance(`MN4 96 not2 0 0 ${nmosModel.modelName}`),
            ];
            break;
        case "nandGate":
            subcircuitChildren = [
                declareInstance(`MP1 ${outputNode} ${input1Node} vdd vdd ${pmosModel.modelName}`),
                declareInstance(`MN1 ${outputNode} ${input1Node} 99 99 ${nmosModel.modelName}`),
                declareInstance(`MN2 99 ${input2Node} 0 0 ${nmosModel.modelName}`),
                declareInstance(`MP2 ${outputNode} ${input2Node} vdd vdd ${pmosModel.modelName}`),
            ];
            break;
        case "norGate":
            subcircuitChildren = [
                declareInstance(`MN1 ${outputNode} ${input1Node} 0 0 ${nmosModel.modelName}`),
                declareInstance(`MN2 ${outputNode} ${input2Node} 0 0 ${nmosModel.modelName}`),
                declareInstance(`MP1 ${outputNode} ${input1Node} 99 99 ${pmosModel.modelName}`),
                declareInstance(`MP2 99 ${input2Node} vdd vdd ${pmosModel.modelName}`),
            ];
            break;
    }
    subcircuitChildren.push(declareInstance(`V0 vdd 0 DC ${device.model.deviceParams.highVoltage}`));
    // Get and store vector names
    const input1CurrentVecName = currentVectorName(subcircuit.designator, input1Node.toString());
    const input2CurrentVecName = currentVectorName(subcircuit.designator, input2Node.toString());
    const outputCurrentVecName = currentVectorName(subcircuit.designator, outputNode.toString());
    const input1VoltageVecName = voltageVectorName(input1);
    const input2VoltageVecName = voltageVectorName(input2);
    const outputVoltageVecName = voltageVectorName(output);
    context.addCurrentVectorName(device, input1CurrentVecName, "input1");
    context.addCurrentVectorName(device, input2CurrentVecName, "input2");
    context.addCurrentVectorName(device, outputCurrentVecName, "output");
    context.addVoltageVectorName(device, input1VoltageVecName, "input1");
    context.addVoltageVectorName(device, input2VoltageVecName, "input2");
    context.addVoltageVectorName(device, outputVoltageVecName, "output");
    return ok([
        declareFETModel(nmosModel),
        declareFETModel(pmosModel),
        declareSubcircuit(subcircuit.subName, subcircuit.externalNodes, subcircuitChildren),
        declareInstance(`${subcircuit.designator} ${input1} ${input2} ${output} ${subcircuit.subName}`),
    ]);
}
function behavioralVoltageSourceToLines(device, context) {
    const ensureResult = ensureNodes(device);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    const currentVecName = currentVectorName(device.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(device, currentVecName);
    context.addVoltageVectorName(device, nPlusVecName, "nPlus");
    context.addVoltageVectorName(device, nMinusVecName, "nMinus");
    return ok(declareInstance(`${device.designator} ${nPlus} ${nMinus} vol={${device.model.deviceParams.expression}}`));
}
function behavioralCurrentSourceToLines(device, context) {
    const ensureResult = ensureNodes(device);
    if (!ensureResult.ok)
        return ensureResult;
    const { nPlus, nMinus } = ensureResult.value;
    const currentVecName = currentVectorName(device.designator);
    const nPlusVecName = voltageVectorName(nPlus);
    const nMinusVecName = voltageVectorName(nMinus);
    context.addCurrentVectorName(device, currentVecName);
    context.addVoltageVectorName(device, nPlusVecName, "nPlus");
    context.addVoltageVectorName(device, nMinusVecName, "nMinus");
    return ok(declareInstance(`${device.designator} ${nPlus} ${nMinus} cur={${device.model.deviceParams.expression}}`));
}
function simpleOpAmpInstanceToLines(device, context) {
    const ensureResult = ensureNodes(device);
    if (!ensureResult.ok)
        return ensureResult;
    const { nonInvertingInput, invertingInput, output } = ensureResult.value;
    // Store instance parameters for later use (e.g. alterations)
    context.addInstanceParameters(device.designator, device.model.deviceParams);
    // Build the subcircuit
    const { subcircuit, subcircuitChildren } = buildOpAmpSubcircuit(device, { ...defaultOpAmpParameters, ...device.model.deviceParams }, context);
    const [nonInvertingInputNode, invertingInputNode, _vccNode, _veeNode, outputNode] = subcircuit.externalNodes;
    // Note: 0 is ground which won't be used, so regular boolean check is sufficient
    if (!nonInvertingInputNode || !invertingInputNode || !outputNode) {
        throw new Error("Op amp subcircuit does not have enough external nodes");
    }
    // Get and store vector names
    const nonInvertingInputCurrentVecName = currentVectorName(subcircuit.designator, nonInvertingInputNode.toString());
    const invertingInputCurrentVecName = currentVectorName(subcircuit.designator, invertingInputNode.toString());
    const outputCurrentVecName = currentVectorName(subcircuit.designator, outputNode.toString());
    const nonInvertingInputVoltageVecName = voltageVectorName(nonInvertingInput);
    const invertingInputVoltageVecName = voltageVectorName(invertingInput);
    const outputVoltageVecName = voltageVectorName(output);
    context.addCurrentVectorName(device, nonInvertingInputCurrentVecName, "nonInvertingInput");
    context.addCurrentVectorName(device, invertingInputCurrentVecName, "invertingInput");
    context.addCurrentVectorName(device, outputCurrentVecName, "output");
    context.addVoltageVectorName(device, nonInvertingInputVoltageVecName, "nonInvertingInput");
    context.addVoltageVectorName(device, invertingInputVoltageVecName, "invertingInput");
    context.addVoltageVectorName(device, outputVoltageVecName, "output");
    const positiveSupplyNode = `int_${device.designator}_vcc`;
    const negativeSupplyNode = `int_${device.designator}_vee`;
    return ok([
        declareSubcircuit(subcircuit.subName, subcircuit.externalNodes, subcircuitChildren),
        // Add voltage sources for vcc and vee
        declareInstance(`VCC_${device.designator} ${positiveSupplyNode} 0 DC ${device.model.deviceParams.positiveSupplyVoltage}`),
        declareInstance(`VEE_${device.designator} ${negativeSupplyNode} 0 DC ${device.model.deviceParams.negativeSupplyVoltage}`),
        declareInstance(`${subcircuit.designator} ${nonInvertingInput} ${invertingInput} ${positiveSupplyNode} ${negativeSupplyNode} ${output} ${subcircuit.subName}`),
    ]);
}
function opAmpInstanceToLines(device, context) {
    const ensureResult = ensureNodes(device);
    if (!ensureResult.ok)
        return ensureResult;
    const { nonInvertingInput, invertingInput, positiveSupply, negativeSupply, output } = ensureResult.value;
    // Store instance parameters for later use (e.g. alterations)
    context.addInstanceParameters(device.designator, device.model.deviceParams);
    // Build the subcircuit
    const { subcircuit, subcircuitChildren } = buildOpAmpSubcircuit(device, device.model.deviceParams, context);
    const [nonInvertingInputNode, invertingInputNode, vccNode, veeNode, outputNode] = subcircuit.externalNodes;
    // Note: 0 is ground which won't be used, so regular boolean check is sufficient
    if (!nonInvertingInputNode || !invertingInputNode || !vccNode || !veeNode || !outputNode) {
        throw new Error("Op amp subcircuit does not have enough external nodes");
    }
    // Get and store vector names
    const nonInvertingInputCurrentVecName = currentVectorName(subcircuit.designator, nonInvertingInputNode.toString());
    const invertingInputCurrentVecName = currentVectorName(subcircuit.designator, invertingInputNode.toString());
    const positiveSupplyCurrentVecName = currentVectorName(subcircuit.designator, vccNode.toString());
    const negativeSupplyCurrentVecName = currentVectorName(subcircuit.designator, veeNode.toString());
    const outputCurrentVecName = currentVectorName(subcircuit.designator, outputNode.toString());
    const nonInvertingInputVoltageVecName = voltageVectorName(nonInvertingInput);
    const invertingInputVoltageVecName = voltageVectorName(invertingInput);
    const positiveSupplyVoltageVecName = voltageVectorName(positiveSupply);
    const negativeSupplyVoltageVecName = voltageVectorName(negativeSupply);
    const outputVoltageVecName = voltageVectorName(output);
    context.addCurrentVectorName(device, nonInvertingInputCurrentVecName, "nonInvertingInput");
    context.addCurrentVectorName(device, invertingInputCurrentVecName, "invertingInput");
    context.addCurrentVectorName(device, positiveSupplyCurrentVecName, "positiveSupply");
    context.addCurrentVectorName(device, negativeSupplyCurrentVecName, "negativeSupply");
    context.addCurrentVectorName(device, outputCurrentVecName, "output");
    context.addVoltageVectorName(device, nonInvertingInputVoltageVecName, "nonInvertingInput");
    context.addVoltageVectorName(device, invertingInputVoltageVecName, "invertingInput");
    context.addVoltageVectorName(device, positiveSupplyVoltageVecName, "positiveSupply");
    context.addVoltageVectorName(device, negativeSupplyVoltageVecName, "negativeSupply");
    context.addVoltageVectorName(device, outputVoltageVecName, "output");
    return ok([
        declareSubcircuit(subcircuit.subName, subcircuit.externalNodes, subcircuitChildren),
        declareInstance(`${subcircuit.designator} ${nonInvertingInput} ${invertingInput} ${positiveSupply} ${negativeSupply} ${output} ${subcircuit.subName}`),
    ]);
}
function buildOpAmpSubcircuit(device, deviceParams, context) {
    const subcircuit = context.getSubcircuitDeclaration("opamp", 5);
    context.addSubcircuitInstanceDesignator(device.designator, subcircuit.designator);
    const [nonInvertingInputNode, invertingInputNode, vccNode, veeNode, outputNode] = subcircuit.externalNodes;
    // Note: 0 is ground which won't be used, so regular boolean check is sufficient
    if (!nonInvertingInputNode || !invertingInputNode || !vccNode || !veeNode || !outputNode) {
        throw new Error("Op amp subcircuit does not have enough external nodes");
    }
    // .subckt uopamp_lvl2 +IN -IN VCC VEE OUT
    // * Universal Opamp Level 2
    // * Single pole opamp with rail saturation, current consumption, current limiting, and input offset voltage
    // *
    // * PINOUT ORDER  1   2   3   4   5
    // * PINOUT ORDER +IN -IN VCC VEE OUT
    // *
    // * Parameters:
    // * Avol => open-loop voltage gain (V/V), default=100k
    // * GBW => gain-bandwidth product (Hz), default=100meg
    // * Rin => differential input resistance (ohm), default=100g
    // * Rout => open-loop output resistance (ohm), default=1
    // * Iq => quiescent supply current (A), default=1m
    // * Ilimit => maximum output current (A), default=1
    // * Vrail => voltage between output saturation and each rail (V), default=0
    // * Vos => input offset voltage (V), default=0
    // * Vmax => total maximum supply voltage between rails (V), default=50
    // *
    // G1 VCC N001 N002 -IN 1u
    // G2 VEE N001 N002 -IN 1u
    // R1 VCC N001 {Avol/1u}
    // R2 N001 VEE {Avol/1u}
    // G3 OUT VCC VCC N001 {1/(2*Rout)}
    // G4 VEE OUT N001 VEE {1/(2*Rout)}
    // R4 VCC OUT {2*Rout}
    // R5 OUT VEE {2*Rout}
    // C1 VCC N001 {1u/(2*pi*GBW)}
    // C2 N001 VEE {1u/(2*pi*GBW)}
    // G6 N005 VEE N001 OUT {1/(2*Rout)}
    // G5 N006 VEE OUT N001 {1/(2*Rout)}
    // R8 -IN +IN {Rin}
    // V6 VCC N003 {Vrail+545m}
    // V7 N008 VEE {Vrail+545m}
    // V8 N002 +IN {Vos}
    // V9 OUT N007 {Ilimit-545m}
    // V10 N004 OUT {Ilimit-545m}
    // D1 N001 N003 diode
    // D2 N008 N001 diode
    // D3 VCC N005 diode
    // D4 VCC N006 diode
    // D5 VEE N005 zener
    // D6 VEE N006 zener
    // D7 N001 N004 diode
    // D8 N007 N001 diode
    // I1 VCC VEE {Iq}
    // .param Avol = 100k
    // .param GBW = 100meg
    // .param Rin = 100g
    // .param Rout = 1
    // .param Iq = 1m
    // .param Ilimit = 1
    // .param Vrail = 0
    // .param Vos = 0
    // .param Vmax = 50
    // .model diode D(Is=1e-14)
    // .model zener D(Is=1e-14 BV={Vmax})
    // .param pi=3.1415926535898
    // .ends
    const { openLoopVoltageGain, gainBandwidthProduct, differentialInputResistance, openLoopOutputResistance, quiescentSupplyCurrent, maxOutputCurrent, headRoom, inputOffsetVoltage, maxSupplyVoltageBetweenRails, } = deviceParams;
    const subcircuitChildren = [
        declareInstance(`G1 ${vccNode} n001 n002 ${invertingInputNode} 1u`),
        declareInstance(`G2 ${veeNode} n001 n002 ${invertingInputNode} 1u`),
        declareInstance(`R1 ${vccNode} n001 ${openLoopVoltageGain / 1e-6}`),
        declareInstance(`R2 n001 ${veeNode} ${openLoopVoltageGain / 1e-6}`),
        declareInstance(`G3 ${outputNode} ${vccNode} ${vccNode} n001 ${1 / (2 * openLoopOutputResistance)}`),
        declareInstance(`G4 ${veeNode} ${outputNode} n001 ${veeNode} ${1 / (2 * openLoopOutputResistance)}`),
        declareInstance(`R4 ${vccNode} ${outputNode} ${2 * openLoopOutputResistance}`),
        declareInstance(`R5 ${outputNode} ${veeNode} ${2 * openLoopOutputResistance}`),
        declareInstance(`C1 ${vccNode} n001 ${1e-6 / (2 * Math.PI * gainBandwidthProduct)}`),
        declareInstance(`C2 n001 ${veeNode} ${1e-6 / (2 * Math.PI * gainBandwidthProduct)}`),
        declareInstance(`G6 n005 ${veeNode} n001 ${outputNode} ${1 / (2 * openLoopOutputResistance)}`),
        declareInstance(`G5 n006 ${veeNode} ${outputNode} n001 ${1 / (2 * openLoopOutputResistance)}`),
        declareInstance(`R8 ${invertingInputNode} ${nonInvertingInputNode} ${differentialInputResistance}`),
        declareInstance(`V6 ${vccNode} n003 ${headRoom + 0.545}`),
        declareInstance(`V7 n008 ${veeNode} ${headRoom + 0.545}`),
        declareInstance(`V8 n002 ${nonInvertingInputNode} ${inputOffsetVoltage}`),
        declareInstance(`V9 ${outputNode} n007 ${maxOutputCurrent - 0.545}`),
        declareInstance(`V10 n004 ${outputNode} ${maxOutputCurrent - 0.545}`),
        declareInstance(`D1 n001 n003 diode`),
        declareInstance(`D2 n008 n001 diode`),
        declareInstance(`D3 ${vccNode} n005 diode`),
        declareInstance(`D4 ${vccNode} n006 diode`),
        declareInstance(`D5 ${veeNode} n005 zener`),
        declareInstance(`D6 ${veeNode} n006 zener`),
        declareInstance(`D7 n001 n004 diode`),
        declareInstance(`D8 n007 n001 diode`),
        declareInstance(`I1 ${vccNode} ${veeNode} ${quiescentSupplyCurrent}`),
        declareModel(".model diode D(Is=1e-14)"),
        declareModel(`.model zener D(Is=1e-14 BV=${maxSupplyVoltageBetweenRails})`),
    ];
    return { subcircuit, subcircuitChildren };
}
function instanceToDeclarations(deviceInstance, context) {
    switch (deviceInstance.type) {
        case "resistorInstance":
            return resistorInstanceToLines(deviceInstance, context);
        case "capacitorInstance":
            return capacitorInstanceToLines(deviceInstance, context);
        case "inductorInstance":
            return inductorInstanceToLines(deviceInstance, context);
        case "coupledInductorInstance":
            return coupledInductorInstanceToLines(deviceInstance, context);
        case "diodeInstance":
            return diodeInstanceToLines(deviceInstance, context);
        case "constantCurrentSourceInstance":
            return constantCurrentSourceToLines(deviceInstance, context);
        case "pulsedCurrentSourceInstance":
            return pulsedCurrentSourceToLines(deviceInstance, context);
        case "sinusoidalCurrentSourceInstance":
            return sinusoidalCurrentSourceToLines(deviceInstance, context);
        case "constantVoltageSourceInstance":
            return constantVoltageSourceToLines(deviceInstance, context);
        case "sinusoidalVoltageSourceInstance":
            return sinusoidalVoltageSourceToLines(deviceInstance, context);
        case "pulsedVoltageSourceInstance":
            return pulsedVoltageSourceToLines(deviceInstance, context);
        case "voltageControlledVoltageSourceInstance":
            return voltageControlledVoltageSourceToLines(deviceInstance, context);
        case "bjtInstance":
            return bjtInstanceToLines(deviceInstance, context);
        case "switchInstance":
            return switchInstanceToLines(deviceInstance, context);
        case "spdtSwitchInstance":
            return spdtSwitchInstanceToLines(deviceInstance, context);
        case "voltageControlledSwitchInstance":
            return voltageControlledSwitchInstanceToLines(deviceInstance, context);
        case "mosfetInstance":
            return mosfetInstanceToLines(deviceInstance, context);
        case "jfetInstance":
            return jfetInstanceToLines(deviceInstance, context);
        case "ammeterInstance":
            return ammeterInstanceToLines(deviceInstance, context);
        case "externalInstance":
            return externalInstanceToLines(deviceInstance, context);
        case "inverterInstance":
            return inverterInstanceToLines(deviceInstance, context);
        case "gateInstance":
            return gateInstanceToLines(deviceInstance, context);
        case "behavioralVoltageSourceInstance":
            return behavioralVoltageSourceToLines(deviceInstance, context);
        case "behavioralCurrentSourceInstance":
            return behavioralCurrentSourceToLines(deviceInstance, context);
        case "opAmpInstance":
            return opAmpInstanceToLines(deviceInstance, context);
        case "simpleOpAmpInstance":
            return simpleOpAmpInstanceToLines(deviceInstance, context);
    }
}
export function netlistLineToString(lineDescriptor) {
    if (lineDescriptor.type === "declareSubcircuit") {
        const subcktTitle = [`.subckt ${lineDescriptor.subName}`, ...lineDescriptor.externalNodes].join(" ");
        return [
            subcktTitle,
            ...lineDescriptor.children.map((child) => child.line),
            `.ends ${lineDescriptor.subName}`,
        ].join("\n");
    }
    return lineDescriptor.line;
}
export function netlistToString(netlist) {
    let lines = netlist.lines.flatMap((instanceLine) => netlistLineToString(instanceLine));
    return [`.title ${netlist.title}`, `.options rshunt=1e11`, `.probe alli`, `${lines.join("\n")}`, `.end`].join("\n");
}
function dedupeDeclarations(lines) {
    const result = [];
    const toDedupe = new Set();
    for (const line of lines) {
        switch (line.type) {
            case "declareInstance":
            case "declareAlteration":
                // Don't dedupe
                result.push(line);
                break;
            case "declareModel":
                if (!toDedupe.has(line.line)) {
                    result.push(line);
                    toDedupe.add(line.line);
                }
                break;
            case "declareSubcircuit":
                if (!toDedupe.has(line.subName)) {
                    result.push(line);
                    toDedupe.add(line.subName);
                }
                break;
        }
    }
    return result;
}
export function fromCircuitDescription(circuit, ctx) {
    const rawLineResults = Object.values(circuit.deviceInstances).map((deviceInstance) => instanceToDeclarations(deviceInstance, ctx));
    // Partition into OK and error results
    const [rawLines, errors] = partitionResults(rawLineResults);
    // If we have any errors, return an error result
    if (errors.length > 0) {
        // Flatten missing connections from all errors
        const missingConnections = errors.flatMap((error) => error.missingConnections);
        return netlistError(missingConnections);
    }
    const dedupedLines = dedupeDeclarations(rawLines.flat());
    return ok({
        title: circuit.circuitName,
        lines: dedupedLines,
    });
}
//# sourceMappingURL=netlist.js.map